/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import utils from '../../../common/utils';
import TokenHelper from '../../../common/TokenHelper';
import * as commonConstants from '../../../common/constants';
import * as studioServiceConstants from '../constants/ServiceConstants';

const CDN_TOKEN_EXPIRY_BUFFER_MINUTES = 10;

export default class TokenService {
  constructor() {
    this.sys2sysExpiry = 0;
    this.sys2sysToken = null;
  }

  static pendingRequests = new Map();

  static requestCounter = 0;

  static awaitEventResult({ requestId, successEvent, errorEvent }) {
    return new Promise((resolve, reject) => {
      let unsubSuccess;
      let unsubError;

      const cleanup = () => {
        if (typeof unsubSuccess === 'function') unsubSuccess();
        if (typeof unsubError === 'function') unsubError();
      };

      const onSuccess = (event) => {
        if (event?.requestId !== requestId) return;
        cleanup();
        resolve(event?.data);
      };

      const onError = (event) => {
        if (event?.requestId !== requestId) return;
        cleanup();
        reject(new Error(event?.message || 'Request failed'));
      };

      unsubSuccess = Framework.getEventManager().on(successEvent, onSuccess);
      unsubError = Framework.getEventManager().on(errorEvent, onError);
    });
  }

  /**
   * Resolves the Stack B prod flag, then delegates to {@link TokenHelper} so studio callers and
   * `utils.resolveBerlinDirectAuthHeaders` share one cached token instead of keeping one each.
   */
  static async getSys2sysToken(forProd, forceRefresh) {
    const { isStackB, env } = utils.getStackBParams();
    const resolvedForProd = forProd === undefined ? isStackB && env === commonConstants.ENV_PROD : Boolean(forProd);
    return TokenHelper.getSys2sysToken(resolvedForProd, forceRefresh);
  }

  static async getCdnToken() {
    const cachedToken = utils.getValidCdnTokenFromCookie(CDN_TOKEN_EXPIRY_BUFFER_MINUTES);
    if (cachedToken) {
      return cachedToken;
    }

    try {
      const pendingKey = 'cdn';
      if (this.pendingRequests.has(pendingKey)) {
        return await this.pendingRequests.get(pendingKey);
      }

      this.requestCounter += 1;
      const requestId = `${Date.now()}_${this.requestCounter}`;

      const promise = (async () => {
        Framework.getEventManager().publish(studioServiceConstants.GET_CDN_TOKEN, {
          payload: { requestId }
        });

        const data = await this.awaitEventResult({
          requestId,
          successEvent: studioServiceConstants.GET_CDN_TOKEN_SUCCESS,
          errorEvent: studioServiceConstants.GET_CDN_TOKEN_ERROR
        });

        if (data?.data?.value) {
          utils.setCDNTokenInCookie(data.data);
          return data.data.value;
        }
        return null;
      })();

      this.pendingRequests.set(pendingKey, promise);
      const result = await promise;
      this.pendingRequests.delete(pendingKey);
      return result;
    } catch (err) {
      const notification = {
        open: true,
        message: 'Error fetching cdn token',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      return null;
    }
  }
}
